'use client';

import { FC, useState } from 'react';

import { ProfileAdTabStatesEnum } from '@/__generated_graphql__/graphql';
import { Card } from '@/components/Card/Card';
import { NoAds } from '@/components/NoAds/NoAds';
import { AdsSkeleton } from '@/components/skeletons/Ad/AdsSkeleton/AdsSkeleton';
import { AD_TYPE, PaymentTypeEnum, ROUTES } from '@/constants/common';
import { useAdActions } from '@/hooks/useAdActions';
import { CardAd } from '@/types';

import { AdvertisePayments } from '../AdvertisePayments/AdvertisePayments';
import * as S from './AdsList.styles';
import { AdsListProps } from './AdsList.types';

const { RESUMES, VACANCIES } = ROUTES;

export const AdsList: FC<AdsListProps> = ({
  activeTab = ProfileAdTabStatesEnum.All,
  cards,
  loading,
  isLoadingMore,
  isProfile,
  colSizes,
  noDataVariant,
  length
}) => {
  const [selectedAd, setSelectedAd] = useState<CardAd | null>(null);
  const [type, setType] = useState<PaymentTypeEnum>(PaymentTypeEnum.ADVERTISE);

  const { onPromote, onActivate, mainModal } = useAdActions(selectedAd);

  const getTypePath = (typename?: string) =>
    typename === AD_TYPE.RESUME ? RESUMES : VACANCIES;

  const getAdditionalProps = (card: CardAd) => {
    if (!isProfile) return {};

    return {
      onPromote: () => {
        onPromote();
        setSelectedAd(card);
        setType(PaymentTypeEnum.ADVERTISE);
      },
      onActivate: () => {
        if (!card.activatable) {
          setSelectedAd(card);
          setType(PaymentTypeEnum.PUBLICATION);
        }
        onActivate(card);
      }
    };
  };

  if (loading) {
    return (
      <S.ListWrapper $cols={colSizes} $length={length}>
        <AdsSkeleton isMine={!!isProfile} />
      </S.ListWrapper>
    );
  }

  if (cards.length) {
    return (
      <>
        {!!selectedAd && (
          <AdvertisePayments
            mainModal={mainModal}
            ad={selectedAd}
            type={type}
            key={type + (selectedAd?.id || '')}
          />
        )}

        <S.ListWrapper $cols={colSizes} $length={length}>
          {cards.map(card => {
            // eslint-disable-next-line no-underscore-dangle
            const typePath = getTypePath(card?.__typename);
            const navigateLink = `${typePath}/${card.id}`;
            const additionalProps = getAdditionalProps(card);

            return (
              <Card
                key={card.id}
                activeTab={activeTab}
                isProfile={isProfile}
                link={navigateLink}
                {...card}
                {...additionalProps}
              />
            );
          })}

          {isLoadingMore && <AdsSkeleton isMine={!!isProfile} />}
        </S.ListWrapper>
      </>
    );
  }
  return <NoAds variant={noDataVariant} />;
};
